import { sortBy } from 'es-toolkit';
import { get } from 'es-toolkit/compat';

import type { JsonBalanceExtended } from 'src/store/shared/balances/types';
import { getLineHue } from 'src/store/utils/color';

import { getGroupMap, getIsSmallBalance, getPrice, getToken, isDla } from '../core/helpers';
import { NO_ASSETS_LABEL } from '../core/constants';
import { getTotal } from '../calculations/totals';
import { generatePieChartData, getDoublingMap } from './helpers';
import { getFilteredBalances } from './filtering';
import { computeAccountHasError } from './getRowWarningInfo';
import type { GetExtendedBalancesTypes } from '../core/types';

export const getNewExtendedBalances = ({
  domain,
  isSmallBalancesVisible = false,
  isDuplicatesVisible = true,
  balances = [],
  coldWallets = [],
  exchanges = [],
  accounts = [],
  brokenAccounts = [],
  selectedTokens = [],
  tokenMap = {},
  prices = {},
}: GetExtendedBalancesTypes): JsonBalanceExtended[] => {
  if (!coldWallets?.length && !balances?.length) {
    return [];
  }

  const cexAccounts = [...accounts, ...brokenAccounts];
  const combinedBalances = [...balances, ...coldWallets];

  if (!combinedBalances.length || !exchanges?.length) {
    return [];
  }

  const walletAccounts =
    coldWallets?.filter((wallet) => wallet.account).map((wallet) => wallet.account!) ?? [];
  const allAccounts = [...cexAccounts, ...walletAccounts];

  if (!allAccounts?.length) {
    return [];
  }

  const tokens = selectedTokens.map((item) => item.token);

  // DLA > MM > OTHER (if DLA are shown)
  // MM > OTHER > DLA (if DLA are hidden)
  const sortBalances = sortBy(combinedBalances, [(item) => !isDla(item.accountName)]);

  const newMap = getDoublingMap({
    balances: sortBalances,
    accounts: allAccounts,
  });

  const result = combinedBalances.map((balance, idx) => {
    const account = allAccounts.find((account) => account.name === balance.accountID);

    const isWallet = balance.isWallet;
    const originalAssetCount = balance.assets.length;
    const assetsSelectedTokens = balance.assets.filter((asset) =>
      tokens.includes(getToken(asset.token, tokenMap)),
    );

    // Pre-filter checks (use original raw assets to detect problems before token filtering)
    const originalAssets = balance.assets || [];

    const acctStatus = account?.status?.valueOf();
    const acctHasMessage =
      typeof account?.message === 'string' && account.message.trim().length > 0;
    const isStatusError = acctStatus === 0 || acctStatus === -2;

    const noAssetsOriginally = originalAssets.length === 0;

    const assets = assetsSelectedTokens.map((asset) => {
      const token = getToken(asset.token, tokenMap);

      // we don't want to have partial doubles
      const isDoubling = !isWallet ? newMap[balance.accountID] : false;

      const tokenPrice = get(prices, [token, 0, 'price'], asset.lastPrice);

      const lastPrice = getPrice({
        price: asset.lastPrice || tokenPrice,
        quotePrice: asset?.quotePrice ?? 1,
        token,
        type: asset.type,
      });

      return {
        ...asset,
        lastPrice,
        token,
        isSmallBalance: getIsSmallBalance(asset),
        volume: asset.volume,
        volumeUSD: asset.volume * lastPrice,
        isDoubling,
      };
    });

    const piechartData = generatePieChartData(assets);

    const isTotalDoubling = !isWallet ? newMap[balance.accountID] : false;

    const exchange = exchanges.find((exchange) => exchange.id === balance.exchangeID);

    const baseItem = assets.find((asset) => asset.type === 1);
    const baseToken = baseItem ? getToken(baseItem.token, tokenMap) : undefined;
    const quoteItem = assets.find((asset) => asset.type === 0);
    const quoteToken = quoteItem ? getToken(quoteItem.token, tokenMap) : undefined;

    const baseTokens = assets.filter((asset) => asset.type === 1);
    const quoteTokens = assets.filter(({ type }) => type !== 1);

    const { value: totalVolumeUSD, tooltip: totalVolumeTooltip } = getTotal(assets, 'volumeUSD', {
      isTotalDoubling,
      isSmallBalancesVisible,
      domain,
    });

    const { value: totalBase } = getTotal(baseTokens, 'volumeUSD', {
      isTotalDoubling: false,
      isSmallBalancesVisible,
      domain,
    });

    const { value: totalQuote } = getTotal(quoteTokens, 'volumeUSD', {
      isTotalDoubling: false,
      isSmallBalancesVisible,
      domain,
    });

    const isAllTokensFiltered = originalAssetCount > 0 && assets.length === 0;

    // Error detection lives in one shared helper so every view agrees
    // (see computeAccountHasError). Missing price does not mark the row as an
    // error — that only shows a per-cell price warning icon.
    const hasError = computeAccountHasError({
      status: acctStatus,
      message: account?.message,
      hasAssets: !noAssetsOriginally,
    });

    const errorMessage = acctHasMessage
      ? account?.message.trim()
      : isStatusError
        ? acctStatus === 0
          ? 'Account suspended'
          : 'Account error'
        : noAssetsOriginally
          ? NO_ASSETS_LABEL
          : undefined;

    const acctStatusVal = account?.status?.valueOf();

    // An account is treated as "small" (hideable by the small-balance toggle) when:
    // - all its tokens were filtered away (e.g. by isIgnoreOtherTokens), leaving zero visible balance, OR
    // - the account is active (status=1) with no visible assets / all assets are individually small, OR
    // - the account is not an error account and all visible assets are small.
    // Error accounts (status=-2) are never marked small — their visibility is governed
    // by the separate "show error accounts" toggle, not the small-balances toggle.
    // Within the status===1 branch `isAllTokensFiltered` is always false (an
    // all-filtered active account is already caught by the first clause above),
    // so the empty-account case reduces to `assets.length === 0`.
    const isTotalSmallBalance =
      (isAllTokensFiltered && acctStatusVal !== -2) ||
      (acctStatusVal === 1
        ? assets.length === 0 || assets.every((asset) => asset.isSmallBalance)
        : acctStatusVal !== -2 &&
          assets.length > 0 &&
          assets.every((asset) => asset.isSmallBalance));

    return {
      ...balance,
      key: balance.accountID,
      assets,
      baseToken: (baseToken ?? '').toUpperCase(),
      quoteToken: (quoteToken ?? '').toUpperCase(),
      piechartData,

      isTotalDoubling,
      isDla: isDla(balance.accountName),
      totalVolumeUSD,
      totalVolumeTooltip,
      total: {
        quoteUSD: totalQuote,
        baseUSD: totalBase,
      },
      isTotalSmallBalance,
      isAllTokensFiltered,
      hasError,
      errorMessage,
      exchange,
      account,
      hue: getLineHue(idx),
      groupMap: getGroupMap({ exchange, account, isWallet }),
      updated: balance.updated,
    };
  });

  return getFilteredBalances<(typeof result)[number]>({
    data: result,
    isSmallBalancesVisible,
    isDuplicatesVisible,
  });
};
